import { API_BASE } from "../utils/api";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";

const DEBT_TYPES = {
  credit_card:   { label: "Credit Card",    icon: "💳", color: "text-red-600 dark:text-red-400",    bg: "bg-red-50    dark:bg-red-900/20",    border: "border-red-200    dark:border-red-500/30" },
  personal_loan: { label: "Personal Loan",  icon: "🏦", color: "text-amber-600 dark:text-amber-400",bg: "bg-amber-50  dark:bg-amber-900/20",  border: "border-amber-200  dark:border-amber-500/30" },
  home_loan:     { label: "Home Loan",      icon: "🏠", color: "text-blue-600 dark:text-blue-400",  bg: "bg-blue-50   dark:bg-blue-900/20",   border: "border-blue-200   dark:border-blue-500/30" },
  car_loan:      { label: "Car Loan",       icon: "🚗", color: "text-purple-600 dark:text-purple-400",bg:"bg-purple-50 dark:bg-purple-900/20", border: "border-purple-200 dark:border-purple-500/30" },
  student_loan:  { label: "Student Loan",   icon: "🎓", color: "text-teal-600 dark:text-teal-400",  bg: "bg-teal-50   dark:bg-teal-900/20",   border: "border-teal-200   dark:border-teal-500/30" },
  store_account: { label: "Store Account",  icon: "🛍️", color: "text-pink-600 dark:text-pink-400",  bg: "bg-pink-50   dark:bg-pink-900/20",   border: "border-pink-200   dark:border-pink-500/30" },
  overdraft:     { label: "Overdraft",      icon: "⚠️", color: "text-orange-600 dark:text-orange-400",bg:"bg-orange-50 dark:bg-orange-900/20",border: "border-orange-200 dark:border-orange-500/30" },
  other:         { label: "Other",          icon: "📋", color: "text-luxury-secondary",              bg: "bg-luxury-elevated",                 border: "border-luxury-border" },
};

const DUE_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

function fmt(n) {
  return "R " + Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function monthsToPayoff(balance, minPayment, annualRate) {
  if (!balance || !minPayment || minPayment <= 0) return null;
  const r = annualRate / 100 / 12;
  if (r === 0) return Math.ceil(balance / minPayment);
  if (minPayment <= balance * r) return null; // Never pays off
  return Math.ceil(-Math.log(1 - (balance * r) / minPayment) / Math.log(1 + r));
}

function AddDebtModal({ onClose, onAdd, token }) {
  const [form, setForm] = useState({
    name: "", debt_type: "credit_card", total_balance: "",
    current_balance: "", interest_rate: "", minimum_payment: "",
    due_day: "25", lender: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.current_balance || !form.minimum_payment) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        total_balance:   parseFloat(form.total_balance)   || parseFloat(form.current_balance) || 0,
        current_balance: parseFloat(form.current_balance) || 0,
        interest_rate:   parseFloat(form.interest_rate)   || 0,
        minimum_payment: parseFloat(form.minimum_payment) || 0,
        due_day:         parseInt(form.due_day) || 25,
      };
      const res = await fetch(`${API_BASE}/api/debt/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        onAdd(await res.json());
        onClose();
      } else {
        alert("Failed to save — make sure the backend server is running on port 8000.");
      }
    } catch (err) {
      console.error(err);
      alert("Cannot connect to backend. Start the server:\ncd ~/Desktop/TaxVaultGlobal/backend && source venv/bin/activate && uvicorn main:app --reload --port 8000");
    }
    finally { setSaving(false); }
  };

  const inp = "w-full px-3 py-2 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 placeholder-luxury-muted";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-luxury-surface border border-luxury-border shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-luxury-border bg-luxury-elevated">
          <div>
            <h3 className="font-bold text-luxury-text text-sm">Add Credit / Debt</h3>
            <p className="text-xs text-luxury-muted mt-0.5">Saved permanently to your account</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-luxury-muted hover:text-luxury-text transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Name / Nickname *</label>
              <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. FNB Credit Card" className={inp} />
            </div>
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Type *</label>
              <select value={form.debt_type} onChange={e => set("debt_type", e.target.value)} className={inp}>
                {Object.entries(DEBT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Current Balance (R) *</label>
              <input type="number" value={form.current_balance} onChange={e => set("current_balance", e.target.value)} placeholder="e.g. 15000" className={inp} />
            </div>
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Original Balance (R)</label>
              <input type="number" value={form.total_balance} onChange={e => set("total_balance", e.target.value)} placeholder="Same as current if unknown" className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Interest Rate (% p.a.) *</label>
              <input type="number" value={form.interest_rate} onChange={e => set("interest_rate", e.target.value)} placeholder="e.g. 21.5" className={inp} />
            </div>
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Min. Monthly Payment (R) *</label>
              <input type="number" value={form.minimum_payment} onChange={e => set("minimum_payment", e.target.value)} placeholder="e.g. 450" className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Payment Due Day</label>
              <select value={form.due_day} onChange={e => set("due_day", e.target.value)} className={inp}>
                {DUE_DAYS.map(d => <option key={d} value={d}>{d}{d===1?"st":d===2?"nd":d===3?"rd":"th"} of month</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Lender / Institution</label>
              <input value={form.lender} onChange={e => set("lender", e.target.value)} placeholder="e.g. FNB, Nedbank" className={inp} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-luxury-secondary uppercase tracking-wide mb-1.5 block">Notes (optional)</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} placeholder="Any notes..." className={inp + " resize-none"} />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-luxury-border bg-luxury-elevated flex gap-3">
          <button onClick={submit} disabled={saving || !form.name || !form.current_balance || !form.minimum_payment}
            className="flex-1 py-2.5 rounded-xl bg-luxury-cta hover:bg-luxury-cta-hover disabled:opacity-40 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2">
            {saving ? <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> : null}
            {saving ? "Saving..." : "Add Debt"}
          </button>
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-luxury-bg border border-luxury-border text-luxury-secondary text-sm font-semibold hover:bg-luxury-elevated transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function CreditDebt() {
  const { token }        = useAuth();
  const { addToast }     = useToast();
  const [debts, setDebts]         = useState([]);
  const [summary, setSummary]     = useState({});
  const [loading, setLoading]     = useState(true);
  const [backendDown, setBackendDown] = useState(false);
  const [showAdd, setShowAdd]     = useState(false);
  const [expanded, setExpanded]   = useState(null);
  const [filter, setFilter]       = useState("all");

  const fetchDebts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/debt/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDebts(data.debts || []);
        setSummary(data.summary || {});
        setBackendDown(false);
      } else {
        setBackendDown(true);
      }
    } catch (err) {
      console.error(err);
      setBackendDown(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDebts(); }, [fetchDebts]);

  const handleAdd = (debt) => {
    setDebts(p => [debt, ...p]);
    setSummary(p => ({ ...p, total_debts: (p.total_debts || 0) + 1 }));
    addToast("Debt added successfully", "success");
  };

  const handlePaidOff = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/debt/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_paid_off: true }),
      });
      if (res.ok) {
        setDebts(p => p.map(d => d.id === id ? { ...d, is_paid_off: true } : d));
        addToast("🎉 Debt marked as paid off!", "success");
      }
    } catch (err) { addToast("Failed to update", "error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this debt?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/debt/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setDebts(p => p.filter(d => d.id !== id));
        addToast("Debt deleted", "success");
      }
    } catch (err) { addToast("Failed to delete", "error"); }
  };

  const filtered = debts.filter(d => {
    if (filter === "active")  return !d.is_paid_off;
    if (filter === "paid")    return d.is_paid_off;
    if (filter !== "all")     return d.debt_type === filter;
    return true;
  });

  return (
    <div className="min-h-screen bg-luxury-bg p-6 lg:p-8">

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-luxury-elevated border border-luxury-gold/30 flex items-center justify-center">
                <svg className="w-4 h-4 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-luxury-muted">Personal Finance</span>
            </div>
            <h1 className="text-2xl font-bold text-luxury-text">Credit & Debt Tracker</h1>
            <p className="text-sm text-luxury-secondary mt-1">Track all your debts, credit cards, and loans in one place.</p>
          </div>
          <button onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 bg-luxury-cta hover:bg-luxury-cta-hover text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm active:scale-95">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add Debt
          </button>
        </div>
      </div>

      {/* ── Backend offline warning ──────────────────────────────── */}
      {backendDown && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/10 px-5 py-4 mb-6 flex items-start gap-3">
          <span className="text-xl flex-shrink-0">⚠️</span>
          <div>
            <p className="text-sm font-bold text-amber-700 dark:text-amber-400">Backend not connected</p>
            <p className="text-xs text-luxury-secondary mt-0.5">
              Start the backend on your Mac to save and load debts:
            </p>
            <code className="text-xs bg-luxury-elevated px-2 py-1 rounded mt-1 block text-luxury-text">
              cd ~/Desktop/TaxVaultGlobal/backend && source venv/bin/activate && uvicorn main:app --reload --port 8000
            </code>
          </div>
        </div>
      )}

      {/* ── Stats ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Debts",      value: summary.total_debts || 0,                                    color: "text-luxury-gold",                         accent: "border-t-luxury-gold" },
          { label: "Total Balance",    value: fmt(summary.total_balance),                                  color: "text-red-600 dark:text-red-400",            accent: "border-t-red-400" },
          { label: "Min. Monthly Pay", value: fmt(summary.total_min_payment),                              color: "text-amber-600 dark:text-amber-400",        accent: "border-t-amber-400" },
          { label: "Paid Off",         value: summary.paid_off || 0,                                       color: "text-luxury-cta",                          accent: "border-t-luxury-cta" },
        ].map(s => (
          <div key={s.label} className={`rounded-2xl p-5 border border-luxury-border border-t-2 bg-luxury-surface transition-all hover:scale-[1.02] ${s.accent}`}>
            <div className={`text-xl lg:text-2xl font-bold mb-1 ${s.color}`}>{s.value}</div>
            <div className="text-xs font-medium text-luxury-secondary uppercase tracking-wide">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filter Tabs ──────────────────────────────────────────── */}
      <div className="flex gap-2 flex-wrap mb-5">
        {[
          { key: "all",    label: "All" },
          { key: "active", label: "Active" },
          { key: "paid",   label: "Paid Off" },
          ...Object.entries(DEBT_TYPES).map(([k, v]) => ({ key: k, label: v.icon + " " + v.label })),
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all
              ${filter === f.key ? "bg-luxury-cta text-white border-transparent" : "bg-luxury-surface border-luxury-border text-luxury-secondary hover:bg-luxury-elevated"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Debt Cards ───────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 rounded-2xl animate-pulse bg-luxury-surface border border-luxury-border" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-14 text-center">
          <div className="text-4xl mb-3">💳</div>
          <p className="font-semibold text-luxury-text mb-1">No debts tracked yet</p>
          <p className="text-sm text-luxury-secondary mb-5">Add your credit cards, loans and accounts to start tracking.</p>
          <button onClick={() => setShowAdd(true)} className="px-5 py-2.5 bg-luxury-cta hover:bg-luxury-cta-hover text-white text-sm font-semibold rounded-xl transition-colors">
            Add First Debt
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(debt => {
            const cfg    = DEBT_TYPES[debt.debt_type] || DEBT_TYPES.other;
            const paidPct = debt.total_balance > 0 ? Math.round(((debt.total_balance - debt.current_balance) / debt.total_balance) * 100) : 0;
            const months = monthsToPayoff(debt.current_balance, debt.minimum_payment, debt.interest_rate);
            const isOpen = expanded === debt.id;

            return (
              <div key={debt.id}
                className={`rounded-2xl bg-luxury-surface border border-luxury-border overflow-hidden transition-all
                  ${debt.is_paid_off ? "opacity-60" : "hover:border-luxury-gold/20"}`}>

                <button onClick={() => setExpanded(isOpen ? null : debt.id)}
                  className="w-full px-5 py-4 flex items-center gap-4 text-left">

                  {/* Icon */}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border text-lg ${cfg.bg} ${cfg.border}`}>
                    {cfg.icon}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-semibold text-sm text-luxury-text">{debt.name}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`}>{cfg.label}</span>
                      {debt.is_paid_off && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-luxury-cta/10 text-luxury-cta border border-luxury-cta/20">✅ Paid Off</span>}
                    </div>
                    {debt.lender && <p className="text-xs text-luxury-muted">{debt.lender}</p>}
                    {/* Progress bar */}
                    {debt.total_balance > 0 && !debt.is_paid_off && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-luxury-elevated overflow-hidden">
                          <div className="h-full rounded-full bg-luxury-cta transition-all" style={{ width: `${paidPct}%` }} />
                        </div>
                        <span className="text-xs text-luxury-muted">{paidPct}% paid</span>
                      </div>
                    )}
                  </div>

                  {/* Balance & rate */}
                  <div className="text-right flex-shrink-0">
                    <div className={`text-sm font-bold ${debt.is_paid_off ? "text-luxury-cta" : "text-red-600 dark:text-red-400"}`}>
                      {debt.is_paid_off ? "Paid Off" : fmt(debt.current_balance)}
                    </div>
                    {!debt.is_paid_off && <div className="text-xs text-luxury-muted">{debt.interest_rate}% p.a.</div>}
                  </div>

                  <svg className={`w-4 h-4 text-luxury-muted transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isOpen && (
                  <div className="px-5 pb-4 border-t border-luxury-border bg-luxury-elevated">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-4">
                      {[
                        { label: "Current Balance", value: fmt(debt.current_balance) },
                        { label: "Min. Payment",     value: fmt(debt.minimum_payment) },
                        { label: "Interest Rate",    value: `${debt.interest_rate}% p.a.` },
                        { label: "Payment Due",      value: `${debt.due_day}${debt.due_day===1?"st":debt.due_day===2?"nd":debt.due_day===3?"rd":"th"} of month` },
                      ].map(f => (
                        <div key={f.label}>
                          <p className="text-xs font-semibold text-luxury-muted uppercase tracking-wide mb-1">{f.label}</p>
                          <p className="text-sm font-semibold text-luxury-text">{f.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Payoff estimate */}
                    {!debt.is_paid_off && months && (
                      <div className="rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/10 px-4 py-3 mb-3">
                        <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                          ⏱️ At minimum payments: paid off in ~{months} months ({Math.ceil(months/12)} year{Math.ceil(months/12)!==1?"s":""})
                        </p>
                        <p className="text-xs text-luxury-secondary mt-0.5">
                          Pay more than the minimum to save on interest.
                        </p>
                      </div>
                    )}

                    {debt.notes && (
                      <p className="text-xs text-luxury-secondary mb-3">{debt.notes}</p>
                    )}

                    <div className="flex gap-2 flex-wrap">
                      {!debt.is_paid_off && (
                        <button onClick={() => handlePaidOff(debt.id)}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-luxury-cta hover:bg-luxury-cta-hover text-white transition-colors">
                          ✅ Mark as Paid Off
                        </button>
                      )}
                      <button onClick={() => handleDelete(debt.id)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-luxury-surface border border-red-200 dark:border-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Tips ─────────────────────────────────────────────────── */}
      {debts.length > 0 && !loading && (
        <div className="mt-6 rounded-2xl bg-luxury-surface border border-luxury-border p-5">
          <h3 className="font-semibold text-luxury-text text-sm mb-3">💡 Debt Payoff Tips — South Africa</h3>
          <div className="space-y-2">
            {[
              "Use the Avalanche Method — pay off highest interest debt first to save the most money",
              "Credit card interest in SA averages 21%+ — always pay more than the minimum",
              "National Credit Act (NCA) protects you — contact a debt counsellor if overwhelmed",
              "Consolidation loan can reduce multiple payments into one lower monthly amount",
              "Nedbank, FNB, Standard Bank all offer debt consolidation — compare rates first",
            ].map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-luxury-elevated flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-luxury-gold">{i+1}</span>
                </span>
                <p className="text-xs text-luxury-secondary">{tip}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAdd && <AddDebtModal onClose={() => setShowAdd(false)} onAdd={handleAdd} token={token} />}
    </div>
  );
}
